import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Mail from "@insite/mobius/Icons/Mail";
import MapPin from "@insite/mobius/Icons/MapPin";
import Phone from "@insite/mobius/Icons/Phone";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    alignment = "alignment",
    title = "title",
    titleLink = "titleLink",
    titleIcon = "titleIcon",
    phoneNumber = "phoneNumber",
    phoneIcon = "phoneIcon",
    emailAddress = "emailAddress",
    emailIcon = "emailIcon",
    address = "address",
    addressIcon = "addressIcon",
}

const iconMap = {
    phone: Phone,
    mail: Mail,
    mapPin: MapPin,
    none: undefined,
} as const;

interface OwnProps extends WidgetProps {
    fields: {
        [fields.alignment]: "left" | "center" | "right";
        [fields.title]: string;
        [fields.titleLink]: LinkFieldValue;
        [fields.titleIcon]: string;
        [fields.phoneNumber]: string;
        [fields.phoneIcon]: keyof typeof iconMap;
        [fields.emailAddress]: string;
        [fields.emailIcon]: keyof typeof iconMap;
        [fields.address]: string;
        [fields.addressIcon]: keyof typeof iconMap;
    };
    extendedStyles?: LinkListStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const titleLink = getLink(state, ownProps.fields.titleLink);
    return {
        titleLink,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface LinkListStyles {
    linkListWrapper?: InjectableCss;
    titleWrapper?: InjectableCss;
    lineWrapper?: InjectableCss;
    linkStyles?: InjectableCss;
    imageStyle?: LazyImageProps;
    iconStyle?: IconPresentationProps;
}

export const linkListStyles: LinkListStyles = {
    linkListWrapper: {
        css: css`
            padding: 10px 0;
        `,
    },
    titleWrapper: {
        css: css`
            font-weight: bold;
        `,
    },
    lineWrapper: {
        css: css`
            display: flex;
            margin-bottom: 10px;
        `,
    },
    linkStyles: {
        css: css`
            font-size: 15px !important;
        `,
    },
    imageStyle: {
        css: css`
            width: 100%;
            height: 100%;
            max-width: 22px;
            max-height: 22px;
            img {
                max-width: 22px;
                max-height: 22px;
            }
        `,
    },
    iconStyle: {
        color: "secondary.contrast",
        css: css`
            width: 100%;
            height: 100%;
            max-width: 22px;
            max-height: 22px;
            img {
                max-width: 22px;
                max-height: 22px;
            }
        `,
    },
};

const FooterContactList: React.FC<Props> = ({ fields, titleLink, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(linkListStyles, extendedStyles));
    const titleImage = <LazyImage src={fields.titleIcon} {...styles.imageStyle} />;
    const phoneIcon = iconMap[fields.phoneIcon || "none"];
    const emailIcon = iconMap[fields.emailIcon || "none"];
    const addressIcon = iconMap[fields.addressIcon || "none"];

    const alignmentStyles = css`
        text-align: ${fields.alignment || "right"};
        ${styles.linkListWrapper?.css}
    `;
    const headerTypo: TypographyPresentationProps = {
        lineHeight: 1,
        size: 22,
        color: "white",
        css: css`
            padding-left: 5px;
        `,
    };

    const bodyTypo: TypographyPresentationProps = {
        color: "white",
        css: css`
            padding-left: 5px;
        `,
    };

    return (
        <StyledWrapper css={alignmentStyles}>
            <StyledWrapper {...styles.titleWrapper}>
                {titleLink ? (
                    <StyledWrapper {...styles.lineWrapper}>
                        {titleImage}
                        <Link
                            typographyProps={headerTypo}
                            color="white"
                            style={{ marginBottom: "15px" }}
                            href={titleLink.url}
                        >
                            {fields.title || titleLink.title}
                        </Link>
                    </StyledWrapper>
                ) : (
                    <StyledWrapper {...styles.lineWrapper}>
                        {titleImage}
                        <Typography as="h3" {...headerTypo} style={{ marginBottom: "15px" }}>
                            {fields.title}
                        </Typography>
                    </StyledWrapper>
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.lineWrapper}>
                <Icon {...styles.iconStyle} size={30} src={phoneIcon} />
                <Typography as="p" {...bodyTypo}>
                    {fields.phoneNumber}
                </Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.lineWrapper}>
                <Icon {...styles.iconStyle} size={30} src={emailIcon} />
                <Typography as="p" {...bodyTypo}>
                    {fields.emailAddress}
                </Typography>
            </StyledWrapper>
            <StyledWrapper {...styles.lineWrapper}>
                <Icon {...styles.iconStyle} size={30} src={addressIcon} />
                <Typography as="p" {...bodyTypo}>
                    {fields.address}
                </Typography>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(FooterContactList),
    definition: {
        group: "Footer",
        icon: "LinkList",
        fieldDefinitions: [
            {
                name: fields.alignment,
                editorTemplate: "DropDownField",
                defaultValue: "left",
                fieldType: "General",
                options: [
                    {
                        displayName: "Left",
                        value: "left",
                    },
                    {
                        displayName: "Center",
                        value: "center",
                    },
                    {
                        displayName: "Right",
                        value: "right",
                    },
                ],
                hideEmptyOption: true,
                sortOrder: 0,
            },
            {
                name: fields.title,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
                sortOrder: 1,
            },
            {
                fieldType: "General",
                name: fields.titleLink,
                displayName: "Title Link",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                sortOrder: 2,
            },
            {
                name: fields.titleIcon,
                fieldType: "General",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                isRequired: true,
                sortOrder: 3,
            },
            {
                name: fields.phoneNumber,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
                sortOrder: 4,
            },
            {
                name: fields.phoneIcon,
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Phone",
                        value: "phone",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 5,
                isRequired: true,
            },
            {
                name: fields.emailAddress,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
                sortOrder: 6,
            },
            {
                name: fields.emailIcon,
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Mail",
                        value: "mail",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 7,
                isRequired: true,
            },
            {
                name: fields.address,
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
                isRequired: true,
                sortOrder: 8,
            },
            {
                name: fields.addressIcon,
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "Map Pin",
                        value: "mapPin",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 9,
                isRequired: true,
            },
        ],
    },
};

export default widgetModule;
